import * as _ from "lodash";
import * as React from "react";
import {MultiSelect} from "@bizform/primereact/components/multiselect/MultiSelect";
import {MultiSelectProps} from "@bizform/primereact/multiselect";
import TooltipOptions from "@bizform/primereact/components/tooltip/TooltipOptions";
import {Static} from "@bizform/type/decorator/Static.decorator";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementDisplayMode,
    ComponentElementStatic,
    ComponentEventType
} from "@bizform/type/model/component_element/ComponentElement.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import {LookUp} from "@bizform/type/model/data_type/LookUp.model";
import {DataType} from "@bizform/type/model/model/DataType.model";
import {
    AccessibilityDropdown,
    ComponentHelper,
    Debug,
    DomHelper,
    ObjectHelper
} from "@bizform/helper/dist/js/bizflow/helper.min";
import {ComponentElementClass, ReactComponentElementInit} from "@bizform/core/dist/js/bizflow/core.min";
import {AbstractComponentElement} from "../AbstractComponentElement";
import {I18n} from "../../misc/model/I18n.model";


let ___dropdown;

type Props = MultiSelectProps & {
};

type WrappedCompState = {
    // attribute
    value?: Array<LookUp>;
    tabindex?: number;
    options?: Array<LookUp>;
    optionLabel?: string;
    style?: object;
    className?: string;
    printStyle?: object;
    printClassName?: string;
    scrollHeight?: string;
    placeholder?: string;
    fixedPlaceholder?: boolean;
    disabled?: boolean;
    filter?: boolean;
    tabIndex?: boolean;
    dataKey?: string;
    appendTo?: HTMLElement;
    tooltipAttribute?: TooltipOptions & {text?: string};
    maxCount?: number;
    // event
    onChange?(e: ComponentEventType): void;
    itemTemplate?(item: any): JSX.Element | undefined;
    selectedItemTemplate?(value: any): JSX.Element | undefined;
};

type WrappedFormState = {
    onChange?: string;
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
    wrappedFormState: WrappedFormState;
};

/**
 * MultiSelect component element
 *
 * @since 0.0.1
 * @author Byungmin Choi, Hwansoon Park
 * @class
 */
@ComponentElementClass()
@Static<ComponentElementStatic<Array<LookUp>>>()
export class BfDropdownMultiple extends AbstractComponentElement<Props, State> {
    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_bVQVrJW8I2HCxTCuwktaQgD4iCLF7vJ",
        category: ComponentCategory.INPUT,
        label: I18n.componentElement.dropdownMultiple.label,
        description: I18n.componentElement.dropdownMultiple.description,
        icon: "",
        dataType: [DataType.LOOKUP],
        onlyArray: true,
        pathToLimitDataTypeArray: "maxCount",
        property: null
    };

    focus(): void {
        if (this.accessibility) {
            this._multiSelect.validationFocus();
        } else if (this._multiSelect && this._multiSelect.container) {
            this._multiSelect.container.querySelector("input").focus();
        }
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            const newState = ComponentHelper.createStateWithProperties<State, ComponentEventType>(
                property,
                this);
            newState.wrappedCompState.filter = newState.wrappedCompState.filter ? true : false;
            newState.wrappedCompState.fixedPlaceholder = newState.wrappedCompState.fixedPlaceholder ? true : false;

            newState.wrappedCompState.maxCount = !newState.wrappedCompState.maxCount ? 1 : newState.wrappedCompState.maxCount;

            let isAcceptable = true;
            if (newState.wrappedCompState.value) {
                if (newState.wrappedCompState.value.length > newState.wrappedCompState.maxCount) {
                    newState.wrappedCompState.value = newState.wrappedCompState.value.slice(0, newState.wrappedCompState.maxCount);
                    isAcceptable = false;
                }
            }
            if (isAcceptable) {
                this.setState(newState);
            } else {
                this.setState(newState, this._callbackSetProperty);
            }
        } catch (e) {
            this.emitDidCatch(e);
        }
    }

    emit(value: Array<LookUp>): void {
    }

    emitFormState(value: string): void {
    }

    subscription(subscriptionValue: Array<LookUp>): void {
        const newState = {
            wrappedCompState: {
                ...this.state.wrappedCompState,
                value: []
            }
        };
        let isAcceptable = true;
        if (!ComponentHelper.isNull(subscriptionValue)) {
            if (Array.isArray(subscriptionValue)) {
                if (subscriptionValue.length > 0) {
                    newState.wrappedCompState.value = subscriptionValue.map((valLookUp) => {
                        const returnValue = newState.wrappedCompState.options.find((item) => {
                            return valLookUp.value === item.value;
                        });
                        if (returnValue) {
                            return returnValue;
                        } else {
                            isAcceptable = false;
                            return null;
                        }
                    });
                }
            } else {
                isAcceptable = false;
            }
        }

        if (isAcceptable) {
            this.setState(newState);
        } else {
            this.emitSubscriptionReject();
        }
    }

    lookupDataSubscription?(lookup: Array<LookUp>): void {
        if (Array.isArray(lookup)) {
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    options: lookup
                }
            };

            this.setState(newState);
        }
    }

    @ReactComponentElementInit(BfDropdownMultiple)
    static init(): ComponentElement<Array<LookUp>> {
        return;
    }

    subscriptionResize(): void {
        if (___dropdown) {
            for (const panelName in ___dropdown) {
                const panel = ___dropdown[panelName];
                if (panel._timer) {
                    clearTimeout(panel._timer);
                }
                panel._timer = setTimeout(panel.alignPanel, 100);
            }
        }
    }

    initDone(): void {
        ___dropdown[this.elementId] = this;
    }

    destroy(): void {
        this.destroyDone();
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    private _multiSelect: any;
    private _multiSelectPrint: any;

    constructor(props?: Props) {
        super(props);
        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {
                options: [],
                value: []
            },
            wrappedFormState: {}
        };

        this._onChangeEvent = this._onChangeEvent.bind(this);
        this._setRef = this._setRef.bind(this);
        this._setRefPrint = this._setRefPrint.bind(this);

        this.subscriptionResize = this.subscriptionResize.bind(this);
        this.closeOverlay = this.closeOverlay.bind(this);
        this.alignPanel = this.alignPanel.bind(this);
        if (!___dropdown) {
            ___dropdown = {};
        }
    }

    private _onChangeEvent(e: ComponentEventType): void {
        const newProps = this.state.wrappedCompState;
        if (this.state.displayMode === ComponentElementDisplayMode.EDIT) {
            let data = newProps.maxCount >= e.value.length ? e.value : newProps.value.length > e.value.length ? e.value : newProps.value;
            this.setState({
                wrappedCompState: {
                    ...newProps,
                    value: [...data]
                }}, this._callbackSetState);
        }
    }

    private _callbackSetProperty(): void {
        this.emit(this.state.wrappedCompState.value);
    }

    private _callbackSetState() {
        try {
            this.emit(this.state.wrappedCompState.value);
            if (this.state.wrappedFormState && this.state.wrappedFormState.onChange) {
                this.emitFormState(this.state.wrappedFormState.onChange);
            }
        } catch (e) {
            Debug.console.error("_callbackSetState", e);
        }
    }

    shouldComponentUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>, nextContext: any): boolean {
        return ObjectHelper.hasDifference(nextState, this.state);
    }

    componentDidUpdate(prevProps, prevState): void {
        if (this._multiSelect && this._multiSelect.container) {
            this._multiSelect.container.removeEventListener("click", ComponentHelper.stopEvent);
            if (this.state.displayMode === ComponentElementDisplayMode.READONLY) {
                this._multiSelect.container.addEventListener("click", ComponentHelper.stopEvent);
            }
            if (this.state.wrappedCompState.tabindex !== this.___tabindex) {
                ComponentHelper.tabindex(this.displayMode, this._multiSelect.container.querySelector("input"), this.state.wrappedCompState.tabindex);
                this.___tabindex = this.state.wrappedCompState.tabindex;
            }
        }
    }

    componentWillUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>) {
        this._removeCustom();
    }

    componentWillUnmount(): void {
        if (this._multiSelect && this._multiSelect.container) {
            this._multiSelect.container.removeEventListener("click", ComponentHelper.stopEvent);
        }
        this._removeCustom();
        this._multiSelect = null;
        delete ___dropdown[this.elementId];
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.emitDidCatch(error);
    }

    emitDidCatch(error: Error | any): void {
        Debug.console.error(error);
    }

    emitSubscriptionReject(): void {
    }

    closeOverlay(): void {
        if (this._multiSelect && typeof this._multiSelect.hide === "function") {
            this._multiSelect.hide();
        }
    }

    alignPanel() {
        if (this._multiSelect && typeof this._multiSelect.alignPanel === "function") {
            this._multiSelect.alignPanel();
        }
    }

    render(): React.ReactNode {
        const {
            show,
            displayMode
        } = this.state;

        if (!show) {
            return null;
        } else {
            switch (displayMode) {
                case ComponentElementDisplayMode.EDIT:
                    return this._render4Edit();
                case ComponentElementDisplayMode.READONLY:
                    return this._render4ReadOnly();
                case ComponentElementDisplayMode.DISABLED:
                    return this._render4Disabled();
                case ComponentElementDisplayMode.PRINT:
                    return this._render4Print();
                default:
                    return null;
            }
        }
    }

    private _render(newProps: WrappedCompState): React.ReactNode {
        delete(newProps["printClassName"]);
        delete(newProps["printStyle"]);
        if (this.accessibility) {
            return (
                <AccessibilityDropdown
                    tabindex={newProps.tabindex}
                    key={this.elementId}
                    id={this.elementId}
                    value={newProps.value}
                    options={newProps.options}
                    placeholder={newProps.placeholder}
                    maxCount={newProps.maxCount}
                    showClear={true}
                    isMultiple={true}
                    displayMode={this.displayMode}
                    tooltipAttribute={newProps.tooltipAttribute}
                    onChange={newProps.onChange}
                    ref={this._setRef}
                />
            );
        } else {
            if (newProps.disabled) {
                newProps.tabIndex = -1 as any;
            }
            const key = ComponentHelper.tooltipProps(newProps, this.elementId, this.accessibility);
            if (Array.isArray(newProps.value)) {
                const v = ObjectHelper.jsonCloneDeep(newProps.value);
                const rV = [];
                for (const key in v) {
                    if (v[key]) {
                        const index = _.findIndex(newProps.options, {value: v[key].value});
                        rV.push(newProps.options[index]);
                    }
                }
                newProps.value = rV;
            }
            return (
                <MultiSelect
                    {...newProps}
                    key={key}
                    id={this.elementId}
                    appendTo={DomHelper.getForOverLayDom()}
                    style={{ ...this.state.wrappedCompState.style, width: "100%" }}
                    optionLabel={"label"}
                    dataKey={"value"}
                    ref={this._setRef}
                />
            );
        }
    }

    private _render4Edit(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState
        };

        return this._render(newProps);
    }

    private _render4ReadOnly(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            readOnly: true
        };

        return this._render(newProps);
    }

    private _render4Disabled(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            disabled: true
        };

        return this._render(newProps);
    }

    private _render4Print(): React.ReactNode {
        let printValue = [];
        this._multiSelect = null;
        if (this.state.wrappedCompState.value) {
            printValue = this.state.wrappedCompState.value.map((val) => {
                return val.label;
            });
        }
        return (
            <div ref={this._setRefPrint} style={this.state.wrappedCompState ? {...this.state.wrappedCompState.printStyle} : null} className={`bf-print-mode ${this.state.wrappedCompState ? this.state.wrappedCompState.printClassName : null}`}>{printValue.join(",")}</div>
        );
    }

    private _setRef(ref) {
        if (ref) {
            this._multiSelect = ref;
            this.readyComponent();
        }
    }

    private _setRefPrint(ref) {
        if (ref) {
            this._multiSelectPrint = ref;
            this.readyComponent();
        }
    }

    private _removeCustom(): void {
        if (this._multiSelect && this._multiSelect.container) {
            if (this._multiSelect && this._multiSelect.container) {
                this._multiSelect.container.removeEventListener("click", ComponentHelper.stopEvent);
            }
        }
    }
}
